import { useState, useRef, useEffect } from 'react';

export default function OtpScreen({
  phone,
  onVerify,
  onChangePhone,
  onResend,
}: {
  phone: string;
  onVerify: () => void;
  onChangePhone: () => void;
  onResend: () => void;
}) {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [timer, setTimer] = useState(120);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (timer > 0) {
      const t = setInterval(() => setTimer((s) => s - 1), 1000);
      return () => clearInterval(t);
    }
  }, [timer]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...code];
    next[index] = value;
    setCode(next);
    setError('');
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const next = text.split('').concat(Array(6 - text.length).fill(''));
    setCode(next as string[]);
    if (text.length >= 6) {
      inputRefs.current[5]?.focus();
    } else {
      inputRefs.current[text.length]?.focus();
    }
  };

  const handleVerify = () => {
    const full = code.join('');
    if (full.length !== 6) {
      setError('Lütfen 6 haneli doğrulama kodunu girin');
      return;
    }
    onVerify();
  };

  const formatPhone = (p: string) => {
    const digits = p.replace(/\D/g, '');
    if (digits.length === 11) {
      return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 9)} ${digits.slice(9, 11)}`;
    }
    return p;
  };

  return (
    <div className="w-full max-w-lg mx-auto">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-soft p-6 md:p-10">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-dark-teal-50 flex items-center justify-center">
            <i className="ri-shield-check-line text-dark-teal-500 text-lg" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-deep-navy-500">Telefon Doğrulama</h2>
            <p className="text-xs text-gray-400">Adım 2/5 — Güvenlik kodu</p>
          </div>
        </div>

        <p className="text-sm text-gray-500 mb-1">
          Güvenliğiniz için{' '}
          <span className="font-semibold text-deep-navy-500">{formatPhone(phone)}</span>{' '}
          numaranıza gönderilen doğrulama kodunu girin.
        </p>

        <div className="flex justify-center gap-2 md:gap-3 my-8" onPaste={handlePaste}>
          {code.map((digit, i) => (
            <input
              key={i}
              ref={(el) => { inputRefs.current[i] = el; }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              className="w-10 h-12 md:w-12 md:h-14 text-center text-lg font-bold text-deep-navy-500 rounded-xl border border-gray-200 focus:outline-none focus:border-dark-teal-500 focus:ring-2 focus:ring-dark-teal-100 transition-all"
            />
          ))}
        </div>

        {error && <p className="text-xs text-red-500 text-center mb-4">{error}</p>}

        <button
          onClick={handleVerify}
          className="w-full py-3.5 rounded-full text-sm font-semibold text-white bg-dark-teal-500 hover:bg-dark-teal-600 transition-colors cursor-pointer mb-4"
        >
          Doğrula ve Devam Et
        </button>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 text-xs">
          {timer > 0 ? (
            <span className="text-gray-400">
              Kodu tekrar gönder: {Math.floor(timer / 60)}:{String(timer % 60).padStart(2, '0')}
            </span>
          ) : (
            <button
              onClick={() => {
                setTimer(120);
                onResend();
              }}
              className="text-dark-teal-500 font-medium hover:underline cursor-pointer"
            >
              Kodu Tekrar Gönder
            </button>
          )}
          <span className="hidden sm:block text-gray-200">|</span>
          <button
            onClick={onChangePhone}
            className="text-gray-500 hover:text-deep-navy-400 transition-colors cursor-pointer"
          >
            Telefon Numarasını Değiştir
          </button>
        </div>
      </div>
    </div>
  );
}